/**
 * Runtime config loader factory.
 *
 * Deployed builds fetch `/config.json` (a static file written per environment),
 * so a single build artifact can point at prod or dev without rebuilding. Local
 * `vite dev` skips the fetch and reads the build-time fallback (import.meta.env)
 * instead.
 *
 * The fetch/merge/cache mechanism is identical across apps; only the config
 * *shape* and fallback differ, so each app builds its own typed fallback and
 * calls `createConfigLoader(fallback)`. `loadConfig()` must resolve before
 * anything reads `getConfig()` (main.tsx awaits it, then imports the app).
 */

// import.meta.env is injected by each consuming app's Vite build; type it
// locally so this package type-checks without a Vite type dependency.
const META_ENV = (import.meta as unknown as { env: { DEV?: boolean } }).env;

export interface ConfigLoader<T> {
  /** Resolve runtime config (dev: fallback; prod: /config.json over fallback). */
  loadConfig: () => Promise<void>;
  /** Read the resolved config. Throws if called before loadConfig() resolves. */
  getConfig: () => T;
}

export function createConfigLoader<T extends object>(
  fallback: T,
  options?: {
    /** Post-process the resolved config (e.g. inject the runtime origin). */
    transform?: (config: T) => T;
  },
): ConfigLoader<T> {
  const finalize = options?.transform ?? ((config: T) => config);
  let config: T | null = null;

  async function loadConfig(): Promise<void> {
    // Local dev reads the direnv-provided import.meta.env, never /config.json.
    if (META_ENV.DEV) {
      config = finalize(fallback);
      return;
    }
    try {
      const res = await fetch('/config.json', { cache: 'no-store' });
      if (res.ok) {
        config = finalize({ ...fallback, ...((await res.json()) as Partial<T>) });
        return;
      }
    } catch {
      // Network/parse failure — fall through to the build-time fallback.
    }
    config = finalize(fallback);
  }

  function getConfig(): T {
    if (!config) {
      throw new Error('getConfig() called before loadConfig() resolved');
    }
    return config;
  }

  return { loadConfig, getConfig };
}
